import { Link } from 'react-router-dom';
import {
  BookOpen,
  HelpCircle,
  Target,
  Shield,
  Filter,
  Clock,
  Copy,
  Activity,
  BarChart3,
  Wallet,
  Zap,
  Settings as SettingsIcon,
  Crown,
  Info,
  Users
} from 'lucide-react';

export default function Tutorial() {
  return (
    <div className="max-w-5xl mx-auto space-y-10 animate-fade-in py-4 sm:py-6 lg:py-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <span className="text-gradient">BetMimic Tutorial</span>
            <BookOpen className="w-7 h-7 text-primary-400" />
          </h1>
          <p className="text-dark-400 mt-2 max-w-2xl">
            Deep-dive guide to how BetMimic works on top of Polymarket: copy configs, risk
            management, positions, trades, subscriptions, promo codes, and what every key
            number in the UI actually means.
          </p>
        </div>
      </div>

      {/* Quick navigation */}
      <div className="card bg-dark-800/70 border-dark-700/60">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          <Link to="#getting-started" className="card card-hover flex items-center gap-3">
          <Wallet className="w-5 h-5 text-primary-400" />
          <div>
            <p className="font-medium">1. Getting Started</p>
            <p className="text-xs text-dark-400">Wallet, subscription, promo codes</p>
          </div>
          </Link>
          <Link to="#copy-configs" className="card card-hover flex items-center gap-3">
          <Target className="w-5 h-5 text-primary-400" />
          <div>
            <p className="font-medium">2. Copy Configs & Risk</p>
            <p className="text-xs text-dark-400">Sizing, filters, limits, SL/TP</p>
          </div>
          </Link>
          <Link to="#positions" className="card card-hover flex items-center gap-3">
          <Activity className="w-5 h-5 text-primary-400" />
          <div>
            <p className="font-medium">3. Positions & Trades</p>
            <p className="text-xs text-dark-400">P&L, closing, auto-management</p>
          </div>
          </Link>
        </div>
      </div>

      {/* Getting Started */}
      <section
        id="getting-started"
        className="space-y-4 pt-6 border-t border-dark-700/60"
      >
        <div className="flex items-center gap-2">
          <Wallet className="w-5 h-5 text-primary-400" />
          <h2 className="text-xl font-semibold">1. Getting Started</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card space-y-2 h-full">
            <h3 className="font-medium flex items-center gap-2">
              <Info className="w-4 h-4 text-primary-400" />
              Before You Start: Wallet, Network &amp; USDC.e
            </h3>
            <p className="text-sm text-dark-400">
              BetMimic sits on top of Polymarket's exchange on the Polygon network. We never
              custody funds — all trades settle in your own Polymarket wallet — but you must
              have the right setup before any copy trading can work:
            </p>
            <ul className="text-sm text-dark-400 space-y-1 list-disc list-inside">
              <li>
                <span className="font-medium">Polygon-compatible wallet</span> — the address
                you trade with on Polymarket (an EOA like MetaMask or Polymarket's email
                wallet). BetMimic uses this exact address for all orders.
              </li>
              <li>
                <span className="font-medium">USDC.e on Polygon</span> — Polymarket collateral
                is USDC.e on Polygon. USDC on other chains or on centralized exchanges is not
                usable until you bridge/deposit it into this wallet.
              </li>
              <li>
                <span className="font-medium">Gas / fees for approvals and trades</span> —
                on-chain approvals and actual trades are real Polygon transactions. Make sure
                your trading or funding setup can pay any required fees so approvals and
                orders don't silently fail.
              </li>
              <li>
                <span className="font-medium">Polymarket account ready</span> — you should
                already be able to place manual trades on polymarket.com with this wallet
                before using BetMimic.
              </li>
            </ul>
            <p className="text-xs text-dark-500">
              If you are new to Polymarket, first create an account, fund it with USDC.e on
              Polygon, and place a small manual trade so you know everything is working
              before turning on copy trading.
            </p>
          </div>

          <div className="card space-y-2 h-full">
            <h3 className="font-medium flex items-center gap-2">
              <Wallet className="w-4 h-4 text-primary-400" />
              Connect Trading Wallet
            </h3>
            <p className="text-sm text-dark-400">
              Go to <span className="font-medium">Settings → Wallet</span> and enter your
              Polymarket trading wallet address, optional proxy/funder address, and private
              key. The key is encrypted and stored, then used by the backend to create a
              dedicated Polymarket CLOB client on startup.
            </p>
            <ul className="text-sm text-dark-400 space-y-1 list-disc list-inside">
              <li><span className="font-medium">Address</span> — your Polymarket wallet on Polygon.</li>
              <li>
                <span className="font-medium">Proxy/Funder</span> — set this if you use an
                email wallet / proxy contract; this address is used to pay for trades.
              </li>
              <li>
                <span className="font-medium">Signature Type</span> — matches how your
                Polymarket wallet signs (EOA, email wallet, proxy).
              </li>
            </ul>
            <p className="text-sm text-dark-400 mt-2">
              After you save wallet settings, the backend spins up a Polymarket client and on
              first startup for a new wallet it will automatically:
            </p>
            <ul className="text-sm text-dark-400 space-y-1 list-disc list-inside">
              <li>
                Derive or create a Polymarket CLOB API key for your wallet so orders can be
                placed programmatically.
              </li>
              <li>
                Check and, if needed, send on-chain <span className="font-mono">approve</span>
                transactions for USDC.e and conditional tokens to Polymarket&apos;s exchange
                contracts on Polygon.
              </li>
              <li>
                Update Polymarket&apos;s internal CLOB balance/allowance tracking so the
                exchange knows how much collateral it can use for your orders.
              </li>
            </ul>
            <p className="text-xs text-dark-500">
              These approval steps happen automatically in the background, but they use your
              wallet and funds. Make sure the wallet is funded with USDC.e (and any required
              gas/fees) before enabling aggressive copy configs.
            </p>
          </div>

          <div className="card space-y-2">
            <h3 className="font-medium flex items-center gap-2">
              <Crown className="w-4 h-4 text-primary-400" />
              Subscription & Promo Codes
            </h3>
            <p className="text-sm text-dark-400">
              Your current plan and limits come from the <span className="font-medium">Subscription</span>
              record in the backend. Plans control how many configs and positions you can run
              at once.
            </p>
            <ul className="text-sm text-dark-400 space-y-1 list-disc list-inside">
              <li>
                <span className="font-medium">Free</span> – 1 copy config, 3 positions,
                10 trades/month.
              </li>
              <li>
                <span className="font-medium">Basic / Pro / Whale</span> – higher limits;
                see <Link to="/pricing" className="text-primary-400">Pricing</Link>.
              </li>
              <li>
                <span className="font-medium">Promo codes</span> – redeem on the
                <Link to="/pricing#promo" className="text-primary-400"> Pricing page</Link>.
                Codes are validated server-side against max uses, validity window, and
                per-user usage.
              </li>
            </ul>
            <p className="text-xs text-dark-500">
              After a successful promo redemption, your subscription tier and features are
              updated immediately and reflected in the UI.
            </p>
          </div>
        </div>
      </section>

      {/* Copy Configs & Risk Management */}
      <section
        id="copy-configs"
        className="space-y-4 pt-6 border-t border-dark-700/60"
      >
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-primary-400" />
          <h2 className="text-xl font-semibold">2. Copy Configs & Risk Management</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card space-y-3 h-full">
            <h3 className="font-medium flex items-center gap-2">
              <Copy className="w-4 h-4 text-primary-400" />
              Position Sizing
            </h3>
            <p className="text-sm text-dark-400">
              In <span className="font-medium">Copy Configs</span> you control how much to
              mirror of a trader&apos;s order. Sizing is interpreted in real dollars (USDC):
            </p>
            <ul className="text-sm text-dark-400 space-y-1 list-disc list-inside">
              <li>
                <span className="font-medium">Fixed</span> – always copy the same dollar
                amount per trade (e.g. $10 per signal).
              </li>
              <li>
                <span className="font-medium">% of Trader Size</span> – scale by the
                trader&apos;s notional (size × price).
              </li>
              <li>
                <span className="font-medium">% of Balance</span> – use a % of your saved
                wallet balance.
              </li>
              <li>
                <span className="font-medium">Min/Max Position Size</span> – hard floor and
                ceiling per position, enforced before any trade is sent.
              </li>
            </ul>
          </div>

          <div className="card space-y-3">
            <h3 className="font-medium flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary-400" />
              Risk Limits (per config)
            </h3>
            <p className="text-sm text-dark-400">
              Each copy config has its own riskManagement block. The engine enforces these
              on every incoming trader trade:
            </p>
            <ul className="text-sm text-dark-400 space-y-1 list-disc list-inside">
              <li>
                <span className="font-medium">Max Exposure</span> – cap total value of open
                positions for this config. New trades are sized down so
                <span className="font-mono"> currentExposure + newSize ≤ maxExposure</span>.
              </li>
              <li>
                <span className="font-medium">Max Open Positions</span> – if you already
                have this many open positions for the config, new signals are skipped.
              </li>
              <li>
                <span className="font-medium">Daily Trade Limit</span> – if &gt; 0, caps how
                many copied trades can execute per calendar day. If set to 0, there is no
                daily cap for that config.
              </li>
              <li>
                <span className="font-medium">Daily Loss Limit</span> – when enabled, if the
                config&apos;s <span className="font-mono">stats.dailyPnL</span> reaches the
                loss amount (in USDC), the engine stops copying new trades for that day.
              </li>
            </ul>
            <p className="text-xs text-dark-500">
              Daily counters (trades + P&amp;L) automatically reset once per UTC day and when
              you run the reset script you created.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="card space-y-3">
            <h3 className="font-medium flex items-center gap-2">
              <Filter className="w-4 h-4 text-primary-400" />
              Market & Trade Filters
            </h3>
            <p className="text-sm text-dark-400">
              Filters decide which trader signals are eligible to be copied:
            </p>
            <ul className="text-sm text-dark-400 space-y-1 list-disc list-inside">
              <li>
                <span className="font-medium">Copy Buys / Sells</span> – you can copy only
                buys, only sells, or both.
              </li>
              <li>
                <span className="font-medium">Min/Max Price</span> – Polymarket prices are
                decimals between 0 and 1 (e.g. 0.52 = 52¢). Trades outside your range are
                skipped.
              </li>
              <li>
                <span className="font-medium">Min Trade Size</span> – trader orders smaller
                than this are ignored.
              </li>
              <li>
                <span className="font-medium">Liquidity / Volume</span> – optional minimum
                order-book liquidity and 24h volume filters, using normalized Polymarket
                market data.
              </li>
              <li>
                <span className="font-medium">Exclude Resolved / Neg-Risk</span> – control
                whether to follow resolved or special neg-risk markets.
              </li>
            </ul>
          </div>

          <div className="card space-y-3">
            <h3 className="font-medium flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary-400" />
              Order Execution
            </h3>
            <p className="text-sm text-dark-400">
              Execution settings map almost 1:1 to Polymarket&rsquo;s CLOB order types:
            </p>
            <ul className="text-sm text-dark-400 space-y-1 list-disc list-inside">
              <li>
                <span className="font-medium">Order Type</span> – FOK, FAK, GTC, or GTD. FOK
                must fill entirely or the engine treats it as a failed copy; GTC/GTD create
                resting limit orders.
              </li>
              <li>
                <span className="font-medium">Price Tolerance</span> – how far you&apos;re
                willing to chase the trader&apos;s price when sending market-like orders.
              </li>
              <li>
                <span className="font-medium">Copy Delay</span> – optional delay (seconds)
                between trader trade and your copy, randomized if enabled.
              </li>
              <li>
                <span className="font-medium">Retries</span> – how many times to retry a
                failed execution before giving up.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Positions, PnL & Auto-Management */}
      <section
        id="positions"
        className="space-y-4 pt-6 border-t border-dark-700/60"
      >
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-primary-400" />
          <h2 className="text-xl font-semibold">3. Positions, P&amp;L &amp; Auto-Management</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="card space-y-3">
            <h3 className="font-medium flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-primary-400" />
              Positions &amp; P&amp;L
            </h3>
            <p className="text-sm text-dark-400">
              The <span className="font-medium">Positions</span> page shows all open and
              historical positions built from copied and manual trades:
            </p>
            <ul className="text-sm text-dark-400 space-y-1 list-disc list-inside">
              <li>
                <span className="font-medium">Size</span> – current number of shares.
              </li>
              <li>
                <span className="font-medium">Avg Price</span> – volume-weighted average
                entry price of all fills.
              </li>
              <li>
                <span className="font-medium">Current Price / Value</span> – pulled from
                Polymarket market data and used to compute unrealized P&amp;L.
              </li>
              <li>
                <span className="font-medium">Unrealized P&amp;L</span> –
                <span className="font-mono">(currentPrice − avgPrice) × size</span>.
              </li>
              <li>
                <span className="font-medium">Realized P&amp;L</span> – accumulated when
                positions are partially or fully closed.
              </li>
            </ul>
          </div>

          <div className="card space-y-3">
            <h3 className="font-medium flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary-400" />
              Stop Loss, Take Profit &amp; Monitoring
            </h3>
            <p className="text-sm text-dark-400">
              The backend runs a position monitor that periodically:
            </p>
            <ul className="text-sm text-dark-400 space-y-1 list-disc list-inside">
              <li>
                Fetches current Polymarket prices for each open position.
              </li>
              <li>
                Checks <span className="font-medium">stop-loss</span> and
                <span className="font-medium">take-profit</span> triggers and, when
                breached, sends the appropriate closing order.
              </li>
              <li>
                Updates the position status (`open`, `stopped_out`, `take_profit`,
                `closed`) and emits real-time socket events to your UI.
              </li>
              <li>
                Updates config-level statistics like total P&amp;L and win/loss counts.
              </li>
            </ul>
            <p className="text-xs text-dark-500">
              You can override SL/TP per position on the <span className="font-medium">Positions</span>
              page; the engine respects those custom triggers on top of config defaults.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="card space-y-3">
            <h3 className="font-medium flex items-center gap-2">
              <Info className="w-4 h-4 text-primary-400" />
              Dashboard Numbers
            </h3>
            <p className="text-sm text-dark-400">
              The <span className="font-medium">Dashboard</span> aggregates:
            </p>
            <ul className="text-sm text-dark-400 space-y-1 list-disc list-inside">
              <li>
                <span className="font-medium">Portfolio Value</span> – sum of
                <span className="font-mono">position.currentValue</span> across open
                positions.
              </li>
              <li>
                <span className="font-medium">Unrealized P&amp;L</span> – sum of unrealized
                P&amp;L for open positions.
              </li>
              <li>
                <span className="font-medium">Active Configs</span> – count of configs with
                <span className="font-mono">isActive=true</span>.
              </li>
              <li>
                <span className="font-medium">Recent Trades</span> – last trades from your
                <span className="font-mono">/positions/trades</span> endpoint plus live
                `tradeExecuted` socket events.
              </li>
            </ul>
          </div>

          <div className="card space-y-3">
            <h3 className="font-medium flex items-center gap-2">
              <Zap className="w-4 h-4 text-primary-400" />
              Manual Trading &amp; Closing
            </h3>
            <p className="text-sm text-dark-400">
              In addition to copied trades, you can manually close positions from the
              <span className="font-medium">Positions</span> page:
            </p>
            <ul className="text-sm text-dark-400 space-y-1 list-disc list-inside">
              <li>
                The <span className="font-medium">Close Position</span> button submits a
                FOK sell order via your Polymarket client for the full position size.
              </li>
              <li>
                If filled, a manual <span className="font-mono">Trade</span> record is
                created and added to the recent trades feed.
              </li>
              <li>
                If the order cannot be filled, the UI surfaces the error returned by the
                backend.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Where to go next */}
      <section
        id="next-steps"
        className="space-y-4 pt-6 border-t border-dark-700/60"
      >
        <div className="flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-primary-400" />
          <h2 className="text-xl font-semibold">4. Next Steps</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link to="/traders" className="card card-hover flex flex-col gap-1">
            <span className="text-sm font-medium flex items-center gap-2">
              <Users className="w-4 h-4 text-primary-400" />
              Find a Trader to Copy
            </span>
            <span className="text-xs text-dark-400">
              Use the leaderboard and analytics to pick a style and risk profile you like.
            </span>
          </Link>
          <Link to="/copy-configs" className="card card-hover flex flex-col gap-1">
            <span className="text-sm font-medium flex items-center gap-2">
              <Target className="w-4 h-4 text-primary-400" />
              Tune Your First Config
            </span>
            <span className="text-xs text-dark-400">
              Start small: low fixed size, conservative max exposure, and enabled stop loss.
            </span>
          </Link>
          <Link to="/pricing" className="card card-hover flex flex-col gap-1">
            <span className="text-sm font-medium flex items-center gap-2">
              <SettingsIcon className="w-4 h-4 text-primary-400" />
              Adjust Plan &amp; Limits
            </span>
            <span className="text-xs text-dark-400">
              Upgrade via crypto payment or promo code when you hit the free-tier limits.
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
}
